import { Box } from '@mui/material';

import { CalendarToolbar } from './components/CalendarToolbar';
import { LegendBar } from './components/LegendBar';
import { MemberFilterSidebar } from './components/MemberFilterSidebar';
import type { CalendarEvent, EventCalendarProps } from './types'; // CalendarEvent used by handleEventClick
import { addDays, addMonths, mondayOf, todayISO } from './utils';
import { MonthView } from './views/MonthView';
import { ScheduleListView } from './views/ScheduleListView';
import { WeekView } from './views/WeekView';

const DEFAULT_HOURS = [
  0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23,
];
const DEFAULT_SLOT_HEIGHT = 48;

export function EventCalendar({
  events,
  members = [],
  view,
  cursor,
  onViewChange,
  onCursorChange,
  selectedMemberIds,
  onSelectedMemberIdsChange,
  showMemberFilter,
  selectedEventId,
  onSelectedEventChange,
  onEventClick,
  onEmptySlotClick,
  onEventDrop,
  onEventDragStart,
  onEventDragEnd,
  canDragEvent,
  renderToolbarActions,
  weekStartsOn = 1,
  hours = DEFAULT_HOURS,
  slotHeight = DEFAULT_SLOT_HEIGHT,
  snapMinutes,
  availableRanges,
  chemistryAvailableRanges,
  showKindHatches,
  allowedViews,
  onReachEnd,
  onReachStart,
  isLoadingInitial,
  isLoadingFuture,
  isLoadingPast,
  pageError,
}: EventCalendarProps) {
  const sidebarVisible =
    showMemberFilter ?? (selectedMemberIds !== undefined && members.length > 0);

  function handleEventClick(event: CalendarEvent) {
    onSelectedEventChange?.(event.id);
    onEventClick?.(event);
  }

  function handleToday() {
    onCursorChange(todayISO());
  }

  function handlePrev() {
    if (view === 'week') {
      onCursorChange(addDays(mondayOf(cursor, weekStartsOn), -7));
    } else {
      onCursorChange(addMonths(cursor, -1));
    }
  }

  function handleNext() {
    if (view === 'week') {
      onCursorChange(addDays(mondayOf(cursor, weekStartsOn), 7));
    } else {
      onCursorChange(addMonths(cursor, 1));
    }
  }

  // The calendar content area (week/month/schedule) — may share horizontal
  // space with the panel and/or the filter sidebar.
  const calendarContent = (
    <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', minHeight: 0 }}>
      {view === 'week' && (
        <WeekView
          cursor={cursor}
          events={events}
          members={members}
          selectedEventId={selectedEventId}
          selectedMemberIds={selectedMemberIds}
          weekStartsOn={weekStartsOn}
          hours={hours}
          slotHeight={slotHeight}
          snapMinutes={snapMinutes}
          onEventClick={handleEventClick}
          onEmptySlotClick={onEmptySlotClick}
          onEventDrop={onEventDrop}
          onEventDragStart={onEventDragStart}
          onEventDragEnd={onEventDragEnd}
          canDragEvent={canDragEvent}
          availableRanges={availableRanges}
          chemistryAvailableRanges={chemistryAvailableRanges}
        />
      )}

      {view === 'month' && (
        <MonthView
          cursor={cursor}
          events={events}
          members={members}
          selectedEventId={selectedEventId}
          selectedMemberIds={selectedMemberIds}
          weekStartsOn={weekStartsOn}
          onEventClick={handleEventClick}
          onDayClick={(date) => onCursorChange(date)}
          onViewChange={onViewChange}
          onCursorChange={onCursorChange}
        />
      )}

      {view === 'schedule' && (
        <ScheduleListView
          cursor={cursor}
          events={events}
          members={members}
          selectedEventId={selectedEventId}
          selectedMemberIds={selectedMemberIds}
          onEventClick={handleEventClick}
          onReachEnd={onReachEnd}
          onReachStart={onReachStart}
          isLoadingInitial={isLoadingInitial}
          isLoadingFuture={isLoadingFuture}
          isLoadingPast={isLoadingPast}
          pageError={pageError}
        />
      )}
    </Box>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        background: '#fafaf8',
        fontFamily: "'Inter', system-ui, sans-serif",
      }}
    >
      <CalendarToolbar
        view={view}
        cursor={cursor}
        weekStartsOn={weekStartsOn}
        onViewChange={onViewChange}
        onToday={handleToday}
        onPrev={handlePrev}
        onNext={handleNext}
        renderToolbarActions={renderToolbarActions}
        allowedViews={allowedViews}
      />

      <LegendBar showKindHatches={showKindHatches} />

      {sidebarVisible ? (
        <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          <MemberFilterSidebar
            members={members}
            selectedMemberIds={selectedMemberIds ?? null}
            onSelectedMemberIdsChange={onSelectedMemberIdsChange ?? (() => {})}
          />
          {calendarContent}
        </Box>
      ) : (
        calendarContent
      )}
    </Box>
  );
}
